import type { LucideIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/core/card';
import { Button } from '../../components/core/button';
import { TextArea } from '../../components/core/textarea';

const SECTION_CARD_CLASS = 'rounded-xl border border-rose-200 border-b-[4px] border-b-rose-300 bg-gradient-to-b from-white to-gray-50/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_0_rgba(0,0,0,0.05),0_6px_12px_rgba(0,0,0,0.04)]';
const SECTION_TITLE_CLASS = 'flex items-center gap-2 text-base font-semibold text-gray-800';
const SECTION_DOT_CLASS = 'mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-rose-300';

export function EditableListCard({
  title,
  items,
  icon: Icon,
  onItemChange,
  onItemAdd,
  onItemDelete,
  editable,
}: {
  title: string;
  items: string[];
  icon?: LucideIcon;
  onItemChange: (index: number, value: string) => void;
  onItemAdd?: () => void;
  onItemDelete?: (index: number) => void;
  editable: boolean;
}) {
  return (
    <Card className={SECTION_CARD_CLASS}>
      <CardHeader className="pb-2">
        <CardTitle className={SECTION_TITLE_CLASS}>
          {Icon && <Icon className="h-4 w-4 text-rose-400" aria-hidden="true" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i}>
              {editable ? (
                <div className="flex items-start gap-1">
                  <TextArea
                    value={item}
                    onChange={(e) => onItemChange(i, e.target.value)}
                    rows={2}
                    className="min-h-0 rounded border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  />
                  {onItemDelete && items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onItemDelete(i)}
                      className="mt-1 h-auto px-1 py-0 text-xs text-red-400 hover:bg-transparent hover:text-red-600"
                      title="Remove item"
                    >
                      &#x2715;
                    </Button>
                  )}
                </div>
              ) : (
                <p className="flex items-start gap-2 text-sm text-gray-700">
                  <span className={SECTION_DOT_CLASS} />
                  {item}
                </p>
              )}
            </li>
          ))}
        </ul>
        {editable && onItemAdd && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onItemAdd}
            className="mt-2 h-auto px-0 py-0 text-xs text-gray-500 hover:bg-transparent hover:text-gray-700"
          >
            + Add
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
